<template>
  <main class="sys-config-page">
    <section class="soft-card table-panel">
      <div class="toolbar">
        <el-input
          v-model="searchQuery"
          placeholder="搜索 Key 或 Value..."
          size="small"
          clearable
          class="search-input"
          @keyup.enter.native="onSearch" @clear="onSearch"
        />
        <el-button size="small" plain icon="el-icon-search" @click="onSearch">搜索</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="onAdd">新增</el-button>
      </div>

      <div class="desktop-only">
        <el-table :data="configs" size="small" style="width: 100%">
          <el-table-column type="index" label="序号" width="70" :index="pageIndex" />
          <el-table-column prop="configKey" label="Key" min-width="200" show-overflow-tooltip>
            <template slot-scope="scope">
              <code class="key-text">{{ scope.row.configKey }}</code>
            </template>
          </el-table-column>
          <el-table-column prop="configValue" label="Value" min-width="320" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="value-text">{{ scope.row.configValue }}</span>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="180">
            <template slot-scope="scope">{{ $formatTime(scope.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="更新时间" width="180">
            <template slot-scope="scope">{{ $formatTime(scope.row.updatedAt) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="200">
            <template slot-scope="scope">
              <el-button size="small" type="text" @click="onEdit(scope.row)">编辑</el-button>
              <el-button size="small" type="text" class="delete-btn" @click="onDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="mobile-only list-cards">
        <div v-for="(row, i) in configs" :key="i" class="list-card">
          <div class="card-header">
            <div>
              <div class="card-title"><code>{{ row.configKey }}</code></div>
            </div>
          </div>
          <div class="value-line">{{ row.configValue }}</div>
          <div class="meta-row">
            <span>创建：{{ $formatTime(row.createdAt) }}</span>
          </div>
          <div class="card-actions">
            <el-button size="small" type="text" @click="onEdit(row)">编辑</el-button>
            <el-button size="small" type="text" class="delete-btn" @click="onDelete(row)">删除</el-button>
          </div>
        </div>
      </div>

      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="onPageChange"
      />

      <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="560px" :close-on-click-modal="false">
        <el-form ref="form" :model="form" :rules="rules" label-width="90px" label-position="right" size="small">
          <el-form-item label="Key" prop="configKey">
            <el-input v-model="form.configKey" :disabled="!!form.id" placeholder="例: billing_node_A" />
          </el-form-item>
          <el-form-item label="Value" prop="configValue">
            <el-input
              v-model="form.configValue"
              type="textarea"
              :autosize="{ minRows: 2, maxRows: 8 }"
              placeholder="例: 1,2,3"
            />
          </el-form-item>
          <div class="form-tip" v-if="!form.id">
            Key 创建后不可修改，请谨慎填写。<br/>
            如 <code>billing_node_A</code> 表示节点 A 负责的客户 ID 列表（逗号分隔）。
          </div>
        </el-form>
        <span slot="footer">
          <el-button @click="dialogVisible = false" size="small">取消</el-button>
          <el-button type="primary" @click="submitForm" size="small" :loading="submitting">确定</el-button>
        </span>
      </el-dialog>
    </section>
  </main>
</template>

<script>
export default {
  name: 'SysConfigPage',
  data() {
    return {
      configs: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      searchQuery: '',
      dialogVisible: false,
      dialogTitle: '',
      submitting: false,
      rules: {
        configKey: [
          { required: true, message: '请输入 Key', trigger: 'blur' },
          { pattern: /^[A-Za-z0-9_.-]+$/, message: '仅支持字母、数字、下划线、点和短横线', trigger: 'blur' }
        ],
        configValue: [
          { required: true, message: '请输入 Value', trigger: 'blur' }
        ]
      },
      form: {
        id: null,
        configKey: '',
        configValue: ''
      }
    }
  },
  computed: {
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    }
  },
  mounted() {
    this.fetchConfigs()
  },
  methods: {
    async fetchConfigs() {
      try {
        const res = await this.$http.get('/admin/sys-config', {
          params: {
            keyword: this.searchQuery || undefined,
            page: this.currentPage,
            pageSize: this.pageSize
          }
        })
        this.configs = res.records || []
        this.total = Number(res.total) || 0
      } catch {
        this.$message.error('加载配置列表失败')
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchConfigs()
    },
    onSearch() {
      this.currentPage = 1
      this.fetchConfigs()
    },
    onAdd() {
      this.dialogTitle = '新增配置'
      this.form = { id: null, configKey: '', configValue: '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    onEdit(row) {
      this.dialogTitle = '编辑配置'
      this.form = { id: row.id, configKey: row.configKey, configValue: row.configValue || '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          if (this.form.id) {
            await this.$http.put('/admin/sys-config/' + this.form.id, { configValue: this.form.configValue })
            this.$message.success('编辑成功')
          } else {
            await this.$http.post('/admin/sys-config', { configKey: this.form.configKey, configValue: this.form.configValue })
            this.$message.success('新增成功')
          }
          this.dialogVisible = false
          this.fetchConfigs()
        } catch (e) {
          const msg = e?.response?.data?.error || '操作失败'
          this.$message.error(msg)
        } finally {
          this.submitting = false
        }
      })
    },
    onDelete(row) {
      this.$confirm('确定删除配置「' + row.configKey + '」？删除后相关功能可能失效。', '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$http.delete('/admin/sys-config/' + row.id)
      }).then(() => {
        this.$message.success('删除成功')
        this.fetchConfigs()
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.sys-config-page {
  padding: 18px 24px 76px;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}

.search-input {
  width: 220px;
}

.delete-btn {
  color: #ff93a4;
}

.delete-btn:hover {
  color: #ffb6c1;
}

.key-text {
  font-size: 13px;
  color: #8eddf8;
  background: rgba(50, 200, 245, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.value-text {
  font-size: 13px;
  color: var(--text);
  word-break: break-all;
}

.form-tip {
  margin-top: -8px;
  padding: 10px 12px;
  font-size: 12px;
  color: var(--muted-2);
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  line-height: 1.6;
}

.form-tip code {
  color: #8eddf8;
  background: rgba(50, 200, 245, 0.1);
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
}

/* ---------- Mobile ---------- */
.list-cards {
  display: grid;
  gap: 14px;
}

.mobile-only { display: none; }

@media (max-width: 760px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .sys-config-page {
    padding: 12px 12px 28px;
  }
  .table-panel {
    padding: 10px;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-input {
    flex: 1;
    min-width: 80px;
  }
  .pagination-bar {
    text-align: center;
  }
}

.list-card {
  padding: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(27,39,58,.92), rgba(20,30,46,.88));
  border: 1px solid rgba(133,164,255,.12);
  box-shadow: 0 8px 28px rgba(0,0,0,.22);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.card-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}

.card-title code {
  color: #8eddf8;
  background: rgba(50, 200, 245, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.value-line {
  font-size: 13px;
  color: var(--text);
  word-break: break-all;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  margin-bottom: 8px;
}

.meta-row {
  font-size: 11px;
  color: rgba(255,255,255,.4);
  padding-top: 6px;
  border-top: 1px solid rgba(133,164,255,0.06);
}

.card-actions {
  display: flex;
  gap: 6px;
  padding-top: 10px;
}

.card-actions .el-button--text {
  font-size: 12px;
  padding: 4px 10px;
}
</style>
